import {
	BusyIndicator,
	ChangeTriggerKind,
	ContextActionBar,
	FloatingActionButton,
	InlineOptionSelectorItem,
	NoResults,
	OptionsMenu,
	PageContentTitle,
	SearchInputForm,
	SearchPlaceholder,
	ToolbarRegularAction
} from "client/Components";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	AlertManager,
	createRemoteDataSource,
	LazyLoadCursorKind,
	MediaQueryKind,
	RemoteDataSourceState,
	useAlerts,
	useDeepComparison,
	useForceRerender,
	useLocalStorageState,
	useUnmountedCancellation,
	useUserScopedKey,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ContactListItem,
	ContactSource,
	deletePersonalContact,
	ExchangeContactSortBy,
	listGlobalContacts,
	listPersonalContacts,
	listWorkspaceContacts,
	WorkspaceContactSortBy
} from "client/Api/Email/contacts";
import { never, UnreachableCaseError } from "client_legacy/ndaw/errors";
import { List } from "./List/List";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { ContactGroupingKind } from "./List/groups";
import { AddressBookPageLayout } from "../AddressBookPageLayout";
import { SourceFilter } from "../SourceSelector";
import { AddressBookOverviewLayoutKind, OverviewLayoutKindSelector } from "./OverviewLayoutKindSelector";
import { DetailsPanel } from "./DetailsPanel";
import {
	ensureNotNil,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { useEditContactDialog } from "../Edit/EditDialog";
import { useDeleteContactConfirmationDialog } from "../DeleteContactConfirmationDialog";
import { getComputedDisplayName } from "../common";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { ContactItemIdentifier } from "./List/ContactItem/ContactItem";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

const PAGE_SIZE = 24;

export type OverviewProps = {
	source: AddressBookContactSource;
	sourceFilter: SourceFilter | undefined;
	onNavigateToItem: (contactId: string, source: AddressBookContactSource) => void;
};

export const Overview = (props: OverviewProps): ReactElement => {
	const {
		source,
		sourceFilter,
		onNavigateToItem
	} = props;

	switch (source) {
		case ContactSource.Personal:
		case ContactSource.Workspace:
			return (
				<DefaultOverview
					source={source}
					sourceFilter={sourceFilter}
					onNavigateToItem={onNavigateToItem}
				/>
			);
		case ContactSource.Gal:
			return (
				<GalOverview
					onNavigateToItem={onNavigateToItem}
				/>
			);
		default:
			never(source);
	}
};
Overview.displayName = "Overview";

type DefaultOverviewProps = {
	source: Exclude<AddressBookContactSource, ContactSource.Gal>;
	sourceFilter: SourceFilter | undefined;
	onNavigateToItem: (contactId: string, source: AddressBookContactSource) => void;
};

const DefaultOverview = (props: DefaultOverviewProps): ReactElement => {
	const {
		source,
		sourceFilter,
		onNavigateToItem
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);

	const filter = useMemo(
		(): FilterState => ({
			source,
			sourceFilter
		}),
		[source, sourceFilter]
	);

	const filtersVersion: number = useDeepComparison([searchTerm, filter]);
	const sortByPersistenceKey: string = useUserScopedKey("address-book:sort-by-field");
	const [selectedSortByField, setSelectedSortByField] = useLocalStorageState<StringKeyOf<ContactListItem>>(
		sortByPersistenceKey,
		"firstName"
	);

	const sortByOptions = useMemo(
		(): readonly InlineOptionSelectorItem<StringKeyOf<ContactListItem>>[] => {
			switch (source) {
				case ContactSource.Workspace:
					return workspaceSortingOptions;
				case ContactSource.Personal:
					return personalContactsSortingOptions;
				default:
					never(source);
			}
		},
		[source]
	);

	const sortByField: StringKeyOf<ContactListItem> = useMemo<StringKeyOf<ContactListItem>>(
		(): StringKeyOf<ContactListItem> => {
			const allSortableFields: StringKeyOf<ContactListItem>[] = sortByOptions.map(s => s.value);

			if (allSortableFields.indexOf(selectedSortByField) === -1) {
				return "firstName";
			}

			return selectedSortByField;
		},
		[selectedSortByField, sortByOptions]
	);

	const {
		currentItems,
		hasMoreOnServer,
		getLoadingState,
		hasLazyLoadError,
		retryToLoadLastPage,
		reload: reloadList
	} = useContactsDataSource({
		searchTerm,
		filtering: filter,
		desiredAmountOfItems,
		sorting: {
			direction: DataSourceSortDirection.Ascending,
			propertyName: sortByField
		}
	});

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems(prevState => prevState + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			setDesiredAmountOfItems(PAGE_SIZE);
		},
		[filtersVersion]
	);

	return (
		<OverviewContent
			currentItems={currentItems}
			searchTerm={searchTerm}
			filter={filter}
			hasLazyLoadError={hasLazyLoadError}
			hasMoreOnServer={hasMoreOnServer}
			sortByField={sortByField}
			sortByOptions={sortByOptions}
			source={source}
			loadingState={getLoadingState()}
			onLoadMore={loadMoreHandler}
			onNavigateToItem={onNavigateToItem}
			onReloadList={reloadList}
			onRetryToLoadLastPage={retryToLoadLastPage}
			onSearchTermChange={setSearchTerm}
			onSortByFieldChange={setSelectedSortByField}
		/>
	);
};
DefaultOverview.displayName = "DefaultOverview";

type GalOverviewProps = {
	onNavigateToItem: (contactId: string, source: AddressBookContactSource) => void;
};

const GalOverview = (props: GalOverviewProps): ReactElement => {
	const {
		onNavigateToItem
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);

	const filtersVersion: number = useDeepComparison([searchTerm]);
	const sortByPersistenceKey: string = useUserScopedKey("address-book:sort-by-field");
	const [selectedSortByField, setSelectedSortByField] = useLocalStorageState<StringKeyOf<ContactListItem>>(
		sortByPersistenceKey,
		"displayName"
	);

	const sortByOptions = useMemo(
		(): readonly InlineOptionSelectorItem<StringKeyOf<ContactListItem>>[] => {
			return galContactsSortingOptions;
		},
		[]
	);

	const sortByField: StringKeyOf<ContactListItem> = useMemo<StringKeyOf<ContactListItem>>(
		(): StringKeyOf<ContactListItem> => {
			const allSortableFields: StringKeyOf<ContactListItem>[] = sortByOptions.map(s => s.value);

			if (allSortableFields.indexOf(selectedSortByField) === -1) {
				return "displayName";
			}

			return selectedSortByField;
		},
		[selectedSortByField, sortByOptions]
	);

	const {
		currentItems,
		hasMoreOnServer,
		getLoadingState,
		hasLazyLoadError,
		retryToLoadLastPage,
		reload: reloadList
	} = useGalContactsDataSource({
		searchTerm,
		desiredAmountOfItems,
		sorting: {
			direction: DataSourceSortDirection.Ascending,
			propertyName: sortByField
		}
	});

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems(prevState => prevState + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			setDesiredAmountOfItems(PAGE_SIZE);
		},
		[filtersVersion]
	);

	return (
		<OverviewContent
			currentItems={currentItems}
			searchTerm={searchTerm}
			hasLazyLoadError={hasLazyLoadError}
			hasMoreOnServer={hasMoreOnServer}
			sortByField={sortByField}
			sortByOptions={sortByOptions}
			source={ContactSource.Gal}
			loadingState={getLoadingState()}
			onLoadMore={loadMoreHandler}
			onNavigateToItem={onNavigateToItem}
			onReloadList={reloadList}
			onRetryToLoadLastPage={retryToLoadLastPage}
			onSearchTermChange={setSearchTerm}
			onSortByFieldChange={setSelectedSortByField}
		/>
	);
};
GalOverview.displayName = "GalOverview";

export type AddressBookContactSource = Exclude<ContactSource, ContactSource.Suggested>;

export type OverviewContentProps = {
	currentItems: readonly ContactListItem[];
	searchTerm: string;
	onSearchTermChange: (term: string) => void;
	loadingState: RemoteDataSourceState;
	source: AddressBookContactSource;
	filter?: FilterState;
	hasMoreOnServer: boolean;
	hasLazyLoadError: boolean;
	sortByField: StringKeyOf<ContactListItem>;
	sortByOptions: readonly InlineOptionSelectorItem<StringKeyOf<ContactListItem>>[];
	onNavigateToItem: (contactId: string, source: AddressBookContactSource) => void;
	onSortByFieldChange: (value: StringKeyOf<ContactListItem>) => void;
	onLoadMore: () => void;
	onReloadList: () => void;
	onRetryToLoadLastPage: (() => void) | undefined;
};

const OverviewContent = (props: OverviewContentProps): ReactElement => {
	const {
		currentItems,
		searchTerm,
		filter,
		loadingState,
		source,
		hasMoreOnServer,
		hasLazyLoadError,
		sortByField,
		sortByOptions,
		onSortByFieldChange,
		onLoadMore,
		onSearchTermChange,
		onNavigateToItem,
		onReloadList,
		onRetryToLoadLastPage
	} = props;

	const [activeItemId, setActiveItemId] = useState<ContactItemIdentifier | null>(null);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [editDialog, openEditDialog] = useEditContactDialog();
	const [deleteConfirmationDialog, opeDeleteConfirmationDialog] = useDeleteContactConfirmationDialog();

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const alertManager: AlertManager = useAlerts();

	const [reloadDetails, detailsVersion] = useForceRerender();

	const layoutKindPersistedKey: string = useUserScopedKey("address-book:overview-layout");
	const [persistedLayoutKind, setPersistedLayoutKind] = useLocalStorageState<AddressBookOverviewLayoutKind>(layoutKindPersistedKey);
	const isDetailsPanelAvailableInViewPort: boolean = useWorkspaceMediaQuery(MediaQueryKind.CompactTablet);

	const layoutKind: AddressBookOverviewLayoutKind = persistedLayoutKind
		?? (isMobile ? AddressBookOverviewLayoutKind.List : AddressBookOverviewLayoutKind.Grid);

	const doesLayoutSupportDetailsPanel: boolean = layoutKind === AddressBookOverviewLayoutKind.List;

	const hasDetailsPanel: boolean = doesLayoutSupportDetailsPanel && isDetailsPanelAvailableInViewPort;

	const createNewPersonalContactHandler = useCallback(
		async (): Promise<void> => {
			if (source !== ContactSource.Personal) {
				throw new Error("Cannot create non personal contact");
			}

			const newContactResult: TryResult<string> = await openEditDialog({});
			if (!newContactResult.wasSuccessful) {
				return;
			}

			onNavigateToItem(
				newContactResult.result,
				ContactSource.Personal
			);
		},
		[
			onNavigateToItem,
			openEditDialog,
			source
		]
	);

	const editContactHandler = useCallback(
		async (contactId: ContactItemIdentifier): Promise<void> => {
			if (source !== ContactSource.Personal) {
				throw new Error("Cannot edit non personal contact");
			}

			const result: TryResult<string> = await openEditDialog({
				exchangeContactId: contactId.id
			});

			if (!result.wasSuccessful) {
				return;
			}

			onReloadList();

			const isSelectedItem: boolean = activeItemId?.id === contactId.id
				&& activeItemId.source === contactId.source;

			if (hasDetailsPanel && isSelectedItem) {
				reloadDetails();
			}
		},
		[
			source,
			hasDetailsPanel,
			activeItemId,
			openEditDialog,
			onReloadList,
			reloadDetails
		]
	);

	const deleteContactHandler = useCallback(
		async (contactId: ContactItemIdentifier): Promise<void> => {
			const item: ContactListItem = ensureNotNil(
				currentItems.find(x => {
					return x.id === contactId.id
						&& source === contactId.source;
				})
			);

			const confirmationResult: boolean = await opeDeleteConfirmationDialog({
				contactDisplayName: getComputedDisplayName(item)
					.computedDisplayName,
				trackingId: `delete-contact-confirmation-dialog`
			});

			if (!confirmationResult) {
				return;
			}

			try {
				setIsBusy(true);
				await deletePersonalContact(contactId.id, globalCancellation);
				alertManager.showSuccess(localize("_SuccessfullyDeletedTheContact"));
				setIsBusy(false);

				const isSelectedItem: boolean = activeItemId?.id === contactId.id
					&& activeItemId.source === contactId.source;

				if (isSelectedItem) {
					setActiveItemId(null);
				}

				onReloadList();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}
				setIsBusy(false);
				alertManager.showError(localize("_FailedToDeleteTheContact"));
				console.error(e);
			}
		},
		[
			alertManager,
			globalCancellation,
			currentItems,
			activeItemId,
			source,
			opeDeleteConfirmationDialog,
			onReloadList
		]
	);

	useEffect(
		(): void => {
			if (!hasDetailsPanel) {
				setActiveItemId(null);
			}
		},
		[hasDetailsPanel]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(source);
			setActiveItemId(null);
		},
		[source]
	);

	const openItemHandler = useCallback(
		(itemId: ContactItemIdentifier): void => {
			if (hasDetailsPanel) {
				setActiveItemId(itemId);
			} else {
				const {
					id,
					source
				} = itemId;

				onNavigateToItem(id, source);
			}
		},
		[
			hasDetailsPanel,
			onNavigateToItem
		]
	);

	const [
		contextualActions,
		mainAction
	] = useMemo(
		(): [
			actions: readonly AddressBookAction[],
			newAction: ToolbarRegularAction<AddressBookActionContext>
		] => {
			return [
				listActions(
					{
						onEdit: editContactHandler,
						onDelete: deleteContactHandler
					}
				),
				getMainAction(createNewPersonalContactHandler)
			];
		},
		[
			createNewPersonalContactHandler,
			deleteContactHandler,
			editContactHandler
		]
	);

	const actionContext = useMemo(
		(): AddressBookActionContext => ({
			canCreate: source === ContactSource.Personal,
			activeItemId
		}),
		[
			activeItemId,
			source
		]
	);

	const fabHandler = useMemo(
		(): (() => void) | undefined => {
			if (mainAction.canExecute(actionContext)) {
				return () => mainAction.execute(actionContext);
			}

			return undefined;
		},
		[
			actionContext,
			mainAction
		]
	);

	const renderResults = (): ReactElement | null => {
		if (currentItems.length > 0) {
			return (
				<>
					<List
						layoutKind={layoutKind}
						source={source}
						items={currentItems}
						activeItemId={activeItemId}
						onOpenItem={openItemHandler}
						itemsGroupBy={convertSortByFieldToContactListGroupByOption(sortByField)}
						hasMoreItems={hasMoreOnServer}
						isBusy={loadingState === "loading"}
						onRequestMoreItems={onLoadMore}
					/>

					{hasLazyLoadError && (
						<NoResults
							trackingId="has-lazy-loading-error"
							icon="gear"
							title={localize("_NotAbleToRetrieveContacts")}
							message={localize("_WeWereUnableToRetrieveYourContacts")}
							onRetry={onRetryToLoadLastPage}
							hasExtraTopPadding
						/>
					)}

					{!hasDetailsPanel && (
						<AiAssistantFabSpacing />
					)}
				</>
			);
		}

		switch (loadingState) {
			case "loading":
				return <BusyIndicator />;
			case "error":
				return (
					<NoResults
						trackingId="has-error"
						icon="gear"
						title={localize("_NotAbleToRetrieveContacts")}
						message={localize("_WeWereUnableToRetrieveYourContacts")}
						onRetry={onReloadList}
						height="fill"
					/>
				);
			default:
		}

		if (searchTerm) {
			return (
				<NoResults
					trackingId="no-search-results"
					icon="magnifying-glass"
					title={localize("_NoSearchResults")}
					message={localize("_PleaseTrySearchingWithAnotherTerm")}
					height="fill"
				/>
			);
		}

		return (
			<NoResults
				trackingId="no-contacts"
				icon="address-book"
				title={localize("_NoContactsYet")}
				message={localize("_ThereAreNoContactsYetInThisAddressBook")}
				height="fill"
			/>
		);
	};

	const shouldReserveSpaceForPlusButton: boolean = isNotNil(fabHandler);

	const title: string = filter
		?.sourceFilter
		?.value ?? sourceToTitle[source];

	return (
		<>
			{isBusy && <BusyIndicator />}
			<AddressBookPageLayout
				trackingId="address-book-overview"
			>
				{isMobile && (
					<ContextActionBar
						actions={contextualActions}
						context={actionContext}
						hasBorderBottom
						trackingId="global-action-bar"
					/>
				)}

				<AddressBookPageLayout.Header>
					<PageContentTitle
						{...(!isResponsive
							? {
								actions: [mainAction],
								actionContext
							}
							: {
								actions: undefined
							}
						)}
						title={title}
					/>

					<AddressBookPageLayout.SearchAndMobileFilter>
						<SearchInputForm
							onChange={onSearchTermChange}
							trigger={{ kind: ChangeTriggerKind.Debounced }}
							trackingId="search-input"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[
										localize("_Name"),
										localize("_Department"),
										localize("_Skills"),
										localize("_Interests"),
										localize("_More").toLowerCase()
									]}
								/>
							)}
							height="large"
							icon="filter"
						/>

						{source !== ContactSource.Gal && (
							<OptionsMenu
								value={sortByField}
								onChange={onSortByFieldChange}
								options={sortByOptions}
								trackingId="contacts-filter"
								title={localize("_SortBy")}
								icon="arrow-up-arrow-down"
								buttonSize="large"
							/>
						)}

						<OverviewLayoutKindSelector value={layoutKind} onChange={setPersistedLayoutKind} />
					</AddressBookPageLayout.SearchAndMobileFilter>
				</AddressBookPageLayout.Header>
				<AddressBookPageLayout.Content
					hasLeftPadding={doesLayoutSupportDetailsPanel && !isMobile}
					hasRightPadding={doesLayoutSupportDetailsPanel && !hasDetailsPanel && !isMobile}
				>
					<AddressBookPageLayout.ItemsPanel
						hasBorder={doesLayoutSupportDetailsPanel && !isMobile}
						shouldReserveSpaceForPlusButton={shouldReserveSpaceForPlusButton}
					>
						{renderResults()}
					</AddressBookPageLayout.ItemsPanel>
					{hasDetailsPanel && (
						<AddressBookPageLayout.DetailsPanel>
							{(activeItemId || currentItems.length > 0) && (
								<DetailsPanel
									key={detailsVersion}
									itemId={activeItemId}
									actions={contextualActions}
									actionContext={actionContext}
								/>
							)}
						</AddressBookPageLayout.DetailsPanel>
					)}
				</AddressBookPageLayout.Content>

			</AddressBookPageLayout>

			{fabHandler && (
				<FloatingActionButton
					icon="plus"
					trackingId="new-contact"
					onClick={fabHandler}
				/>
			)}

			{editDialog}
			{deleteConfirmationDialog}
		</>
	);
};
OverviewContent.displayName = "OverviewContent";

const sourceToTitle: { [key in AddressBookContactSource]: string } = {
	[ContactSource.Personal]: localize("_PersonalContacts"),
	[ContactSource.Gal]: localize("_OrganisationContacts"),
	[ContactSource.Workspace]: localize("_WorkspaceContacts"),
};

const personalContactsSortingOptions: readonly InlineOptionSelectorItem<StringKeyOf<ContactListItem>>[] = [
	{
		text: localize("_FirstName"),
		value: "firstName"
	},
	{
		text: localize("_LastName"),
		value: "lastName"
	},
	{
		text: localize("_JobTitle"),
		value: "jobTitle"
	},
	{
		text: localize("_Department"),
		value: "department"
	},
	{
		text: localize("_Company"),
		value: "company"
	},
	{
		text: localize("_Location"),
		value: "officeLocation"
	}
];

const galContactsSortingOptions: readonly InlineOptionSelectorItem<StringKeyOf<ContactListItem>>[] = [
	{
		text: "",
		value: "displayName"
	}
];

const workspaceSortingOptions: readonly InlineOptionSelectorItem<StringKeyOf<ContactListItem>>[] = [
	{
		text: localize("_FirstName"),
		value: "firstName"
	},
	{
		text: localize("_LastName"),
		value: "lastName"
	},
	{
		text: localize("_JobTitle"),
		value: "jobTitle"
	},
	{
		text: localize("_Department"),
		value: "department"
	},
	{
		text: localize("_Location"),
		value: "officeLocation"
	}
];

type FilterState = {
	source: Exclude<AddressBookContactSource, ContactSource.Gal>;
	sourceFilter: SourceFilter | undefined;
};

const useContactsDataSource = createRemoteDataSource<
	ContactListItem,
	FilterState
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipTake,
	})
	.getHook(
		{
			performRequest: async (options): Promise<LazyLoadPageResult<ContactListItem>> => {
				const {
					filterState: {
						source,
						sourceFilter
					}
				} = options;

				switch (source) {
					case ContactSource.Personal:
						return await listPersonalContacts(
							options.searchTerm,
							options.amount,
							options.skip,
							convertSortingStateToExchangeSortByOption(options.sorting),
							options.signal
						);
					case ContactSource.Workspace:
						return await listWorkspaceContacts(
							options.searchTerm,
							sourceFilter?.kind === "officeLocation" ? sourceFilter.value : null,
							sourceFilter?.kind === "department" ? sourceFilter.value : null,
							options.amount,
							options.skip,
							convertSortingStateToWorkspaceSortByOption(options.sorting),
							options.signal
						);
					default:
						throw new UnreachableCaseError(source);
				}
			}
		}
	);

const useGalContactsDataSource = createRemoteDataSource<
	ContactListItem
>()
	.setConfiguration({
		hasSearch: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipToken,
	})
	.getHook(
		{
			performRequest: async (options): Promise<SkipTokenPageResult<ContactListItem>> => {
				return await listGlobalContacts(
					options.searchTerm,
					options.amount,
					options.skipToken,
					options.signal
				);
			}
		}
	);

function convertSortByFieldToContactListGroupByOption(
	propertyName: StringKeyOf<ContactListItem>
): ContactGroupingKind {
	switch (propertyName) {
		case "firstName":
			return ContactGroupingKind.FirstLetterFirstName;
		case "lastName":
			return ContactGroupingKind.FirstLetterLastName;
		case "jobTitle":
			return ContactGroupingKind.JobTitle;
		case "department":
			return ContactGroupingKind.Department;
		case "company":
			return ContactGroupingKind.Company;
		case "officeLocation":
			return ContactGroupingKind.OfficeLocation;
		case "displayName":
			return ContactGroupingKind.FirstLetterDisplayName;
		default:
			throw new Error(`Grouping on ${propertyName} is not supported`);
	}
}

function convertSortingStateToExchangeSortByOption(
	sortingState: DataSourceSortableState<ContactListItem> | undefined
): ExchangeContactSortBy {
	if (!sortingState || sortingState.direction === DataSourceSortDirection.None) {
		return ExchangeContactSortBy.Default;
	}

	if (sortingState.direction === DataSourceSortDirection.Descending) {
		throw new Error("Only ascending direction is supported");
	}

	switch (sortingState.propertyName) {
		case "firstName":
			return ExchangeContactSortBy.FirstName;
		case "lastName":
			return ExchangeContactSortBy.LastName;
		case "jobTitle":
			return ExchangeContactSortBy.JobTitle;
		case "department":
			return ExchangeContactSortBy.Department;
		case "company":
			return ExchangeContactSortBy.Company;
		case "officeLocation":
			return ExchangeContactSortBy.OfficeLocation;
		case "displayName":
			return ExchangeContactSortBy.Default;
		default:
			throw new Error(`Sorting on ${sortingState.propertyName} is not supported`);
	}
}

function convertSortingStateToWorkspaceSortByOption(
	sortingState: DataSourceSortableState<ContactListItem> | undefined
): WorkspaceContactSortBy {
	if (!sortingState || sortingState.direction === DataSourceSortDirection.None) {
		return WorkspaceContactSortBy.Default;
	}

	if (sortingState.direction === DataSourceSortDirection.Descending) {
		throw new Error("Only ascending direction is supported");
	}

	switch (sortingState.propertyName) {
		case "firstName":
			return WorkspaceContactSortBy.FirstName;
		case "lastName":
			return WorkspaceContactSortBy.LastName;
		case "jobTitle":
			return WorkspaceContactSortBy.JobTitle;
		case "department":
			return WorkspaceContactSortBy.Department;
		case "officeLocation":
			return WorkspaceContactSortBy.OfficeLocation;
		default:
			throw new Error(`Sorting on ${sortingState.propertyName} is not supported`);
	}
}

type AddressBookActionContext = {
	canCreate: boolean;
	activeItemId: ContactItemIdentifier | null;
};

type AddressBookAction = Action<AddressBookActionContext>;

type ActionHandlers = {
	onEdit(itemId: ContactItemIdentifier): void;
	onDelete(itemId: ContactItemIdentifier): void;
};

function listActions(
	handlers: ActionHandlers
): readonly AddressBookAction[] {
	const {
		onEdit,
		onDelete
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "Edit",
			title: localize("_Edit"),
			icon: "pen",
			canExecute: (c) => isNotNil(c.activeItemId)
				&& c.activeItemId.source === ContactSource.Personal,
			execute: (c) => onEdit(ensureNotNil(c.activeItemId))
		},
		{
			kind: ActionKind.Regular,
			name: "Delete",
			title: localize("_Delete"),
			icon: "trash-can",
			canExecute: (c) => isNotNil(c.activeItemId)
				&& c.activeItemId.source === ContactSource.Personal,
			execute: (c) => onDelete(ensureNotNil(c.activeItemId))
		},
	];
}

function getMainAction(
	handler: () => void
): ToolbarRegularAction<AddressBookActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "New",
		title: localize("_New"),
		icon: "plus",
		isPrimary: true,
		canExecute: (c) => c.canCreate,
		execute: () => {
			handler();
		}
	};
}
